/*
 * // Created on 2024-03-20
 * // Created by Vitaut Tryputsin
 * //
 * // Copyright (c) 2024 OPEN CASCADE SAS
 * //
 * // This file is part of commercial software by OPEN CASCADE SAS,
 * // furnished in accordance with the terms and conditions of the contract
 * // and with the inclusion of this copyright notice.
 * // This file or any part thereof may not be provided or otherwise
 * // made available to any third party.
 * //
 * // No ownership title to the software is transferred hereby.
 * //
 * // OPEN CASCADE SAS makes no representation or warranties with respect to the
 * // performance of this software, and specifically disclaims any responsibility
 * // for any damages, special or consequential, connected with its use.
 *
 */
import { Mat4Tools } from "../../../core/geometry/mat4-tools.js";
import { Vec3Tools } from "../../../core/geometry/vec3-tools.js";
import { OperationSide } from "./patient-info.js";
import { StemHeadUtilities } from "./stem-head-utilities.js";
/**
 * Adjust view orientation
*/
export var AdjustViewOrientation;
(function (AdjustViewOrientation) {
    AdjustViewOrientation[AdjustViewOrientation["Sagittal"] = 0] = "Sagittal";
    AdjustViewOrientation[AdjustViewOrientation["Coronal"] = 1] = "Coronal";
    AdjustViewOrientation[AdjustViewOrientation["Axial"] = 2] = "Axial";
})(AdjustViewOrientation || (AdjustViewOrientation = {}));
/**
 * Adjust view helper it helps to calculate camera parameters
 * and the clipping plane normal for each view.
 */
export class AdjustViewHelper {
    constructor(thePatientUtils) { this._myPatientUtils = thePatientUtils; }
    /**
     * Get cup delta
     * @param theOldTransform - old transform
     */
    getCupDelta(theOldTransform) {
        let relativeTransform = Mat4Tools.relativeTransform(this._myPatientUtils.context.current.customTransform.adjustViewShell, theOldTransform);
        const anRotationPoint = this._myPatientUtils.linerCenter;
        const anOldRotationPoint = Vec3Tools.applyMatrix(relativeTransform, anRotationPoint);
        const aDelta = Vec3Tools.subtract(anRotationPoint, anOldRotationPoint);
        return aDelta;
    }
    /**
     * Get initial view parameters
     * @param theOrientation - view orientation
     * @param isStem - is stem
     * @returns adjust view parameters
     */
    getInitialViewParameters(theOrientation, isStem) {
        const anOperationSide = this._myPatientUtils.context.engineering.operationSide;
        switch (theOrientation) {
            case AdjustViewOrientation.Sagittal:
                {
                    let anRightValue = anOperationSide === OperationSide.Right && !isStem ? 1 : -1;
                    let aDirValue = anOperationSide === OperationSide.Left && !isStem ? -1 : 1;
                    const aRightDirection = { x: 0, y: anRightValue, z: 0 };
                    const anUpDirection = { x: 0, y: 0, z: 1 };
                    const aDir = { x: aDirValue, y: 0, z: 0 };
                    const aLocation = { x: 0, y: 0, z: 0 };
                    return { normal: aDir, up: anUpDirection, right: aRightDirection, location: aLocation };
                }
            case AdjustViewOrientation.Coronal:
                {
                    let anRightValue = anOperationSide === OperationSide.Right ? 1 : -1;
                    if (!isStem && anOperationSide === OperationSide.Left) {
                        anRightValue = 1;
                    }
                    const aRightDirection = { x: anRightValue, y: 0, z: 0 };
                    const anUpDirection = { x: 0, y: 0, z: 1 };
                    const aDir = { x: 0, y: anRightValue, z: 0 };
                    const aLocation = { x: 0, y: 0, z: 0 };
                    return { normal: aDir, up: anUpDirection, right: aRightDirection, location: aLocation };
                }
            case AdjustViewOrientation.Axial:
                {
                    let aVal = 1;
                    if (isStem && anOperationSide != OperationSide.Right) {
                        aVal = -1;
                    }
                    const aRightDirection = { x: aVal, y: 0, z: 0 };
                    const anUpDirection = { x: 0, y: aVal, z: 0 };
                    const aDir = { x: 0, y: 0, z: -1 };
                    const aLocation = { x: 0, y: 0, z: 0 };
                    return { normal: aDir, up: anUpDirection, right: aRightDirection, location: aLocation };
                }
        }
    }
    /**
     * Update slice
     * This function returns the new slice parameters for the adjust view.
     * @param theOrientation - view orientation
     * @param isStem - is stem
     * @param theOrigin - origin
     * @param theOldTransform - old transform
     */
    updateSlice(theOrientation, isStem, theOrigin, theOldTransform) {
        const aParams = this.getInitialViewParameters(theOrientation, isStem);
        let aCalculatedParams = this.calculateParameters(aParams, undefined, { x: 0, y: 0, z: 0 }, theOldTransform, isStem);
        if (isStem) {
            let relativeTransform = Mat4Tools.relativeTransform(theOldTransform, this._myPatientUtils.context.current.customTransform.adjustViewStem);
            const anOrigin = Vec3Tools.applyMatrix(relativeTransform, theOrigin);
            aCalculatedParams.location = anOrigin;
        }
        else {
            const delta = this.getCupDelta(theOldTransform);
            const anOrigin = Vec3Tools.add(theOrigin, delta);
            aCalculatedParams.location = anOrigin;
        }
        return aCalculatedParams;
    }
    /**
     * Update camera parameters for slice
     * @param theOrientation - view orientation
     * @param isStem - is stem
     * @param theOldTransform - old transform
     * @param theCamera - camera
     * @returns adjust view parameters
     */
    updateCameraParameters(theOrientation, isStem, theOldTransform, theCamera) {
        const aParams = this.getInitialViewParameters(theOrientation, isStem);
        const aCalculatedParams = this.calculateParameters(aParams, theCamera, { x: 0, y: 0, z: 0 }, theOldTransform, isStem);
        if (!isStem) {
            const aParamsCamera = theCamera.cameraParameters;
            aParamsCamera.direction = aCalculatedParams.normal;
            aParamsCamera.up = aCalculatedParams.up;
            theCamera.cameraParameters = aParamsCamera;
        }
        return aCalculatedParams;
    }
    /**
     * Calculate adjust view parameters
     * @param theAdjustViewParameters - adjust view parameters
     * @param theCamera - camera
     * @param theOrigin - origin
     * @param theOldTransform - old transform
     * @param isStem - is stem
     * @returns adjust view parameters
     */
    calculateParameters(theAdjustViewParameters, theCamera, theOrigin, theOldTransform, isStem) {
        let anOrigin = theOrigin;
        const aDir = theAdjustViewParameters.normal;
        const aRight = theAdjustViewParameters.right;
        const anUp = theAdjustViewParameters.up;
        let aNewDir = aDir;
        let aNewRight = aRight;
        let aNewUp = anUp;
        let aRotationPoint = { x: 0, y: 0, z: 0 };
        let anOldTransform = Mat4Tools.getRotation(theOldTransform);
        let aNewTransform = Mat4Tools.identity();
        if (isStem) {
            const aStemCS = StemHeadUtilities.stemCoordinateSystem;
            const aStemTransform = Mat4Tools.getCSTransformation(aStemCS);
            const anInvertedStemTransform = Mat4Tools.invert(aStemTransform);
            const aStemInitialRotation = Mat4Tools.getRotation(anInvertedStemTransform);
            const aStemCurTransform = this._myPatientUtils.context.current.customTransform.adjustViewStem;
            aRotationPoint = Mat4Tools.getTranslation(aStemCurTransform);
            const aStemCurRotation = Mat4Tools.getRotation(aStemCurTransform);
            aNewTransform = Mat4Tools.multiply(aStemCurRotation, aStemInitialRotation);
            anOldTransform = Mat4Tools.multiply(anOldTransform, aStemInitialRotation);
            aNewDir = Vec3Tools.normalize(Vec3Tools.applyMatrix(aNewTransform, aDir));
            aNewRight = Vec3Tools.normalize(Vec3Tools.applyMatrix(aNewTransform, aRight));
            aNewUp = Vec3Tools.normalize(Vec3Tools.applyMatrix(aNewTransform, anUp));
        }
        if (!theCamera) {
            return { normal: aNewDir, up: aNewUp, right: aNewRight, location: theOrigin };
        }
        if (isStem) {
            const delta = Vec3Tools.subtract(aRotationPoint, Mat4Tools.getTranslation(theOldTransform));
            anOrigin = Vec3Tools.add(theOrigin, delta);
        }
        else {
            const delta = this.getCupDelta(theOldTransform);
            anOrigin = Vec3Tools.add(theOrigin, delta);
        }
        const aMedical = this._myPatientUtils.medicalUtils;
        if (isStem) {
            const aCameraOldUp = theCamera.cameraParameters.up;
            const aCameraDir = theCamera.cameraParameters.direction;
            const anOldRight = Vec3Tools.cross(aCameraDir, aCameraOldUp);
            const aTrsf = aMedical.rotateCameraIfNeeded(aDir, aRight, aNewDir, aNewRight, aRotationPoint, anOldTransform, aNewTransform, theCamera);
            aNewUp = Vec3Tools.applyMatrix(aTrsf, aCameraOldUp);
            aNewRight = Vec3Tools.applyMatrix(aTrsf, anOldRight);
            anOrigin = Vec3Tools.applyMatrix(aTrsf, anOrigin);
        }
        return { normal: aNewDir, up: aNewUp, right: aNewRight, location: anOrigin };
    }
}
